/**
 * GatewayHealth.js — sección "Salud de pasarelas".
 *
 * Herramienta de RIESGO, no de marketing: el operador rota muchas cuentas de
 * Stripe y necesita saber, cuenta por cuenta, cuánto volumen puede empujar hoy
 * sin que se la cierren. Por eso aquí manda la honestidad de los números:
 *
 *   1. Ningún número se inventa en el cliente. Todo lo que se pinta viene de
 *      GET /api/gateway-health. Si el backend manda null, se pinta "--" y se
 *      explica por qué (sin datos suficientes / oculto en este panel), nunca un
 *      0% o un 100% que engañe.
 *   2. Las tasas calculadas sobre pocos intentos se ATENÚAN visualmente (color
 *      apagado + aviso) para que no se lean con la misma confianza que una tasa
 *      sacada de cientos de intentos.
 *   3. El índice de exposición se presenta siempre con su descargo: es un
 *      índice interno de exposición operativa, NO una probabilidad de
 *      suspensión. Lo mismo con la capacidad diaria: es orientativa y no se
 *      aplica en ningún sitio.
 *
 * Los mensajes de alerta del backend vienen en inglés; aquí se reconstruyen en
 * español a partir de los campos estructurados (code / measured / threshold /
 * counts), NO traduciendo a mano el texto. Así, si el backend cambia un umbral,
 * el texto en pantalla sigue siendo correcto. Para un `code` desconocido se cae
 * al `message` original en vez de esconder la alerta.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { G, fmt, fmtDay } from '../theme';
import { gatewayHealthApi } from '../api';

// ── Constantes de presentación ──────────────────────────────────────────────

const PRESETS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: '180d', label: '180d' },
];

const ORDENES = [
  { id: 'riesgo', label: 'Riesgo' },
  { id: 'nombre', label: 'Nombre' },
  { id: 'volumen', label: 'Volumen' },
];

const ESTADOS = {
  healthy: { texto: 'Saludable', badge: 'badge-green', color: G.green },
  warning: { texto: 'Vigilar', badge: 'badge-orange', color: G.orange },
  critical: { texto: 'Crítica', badge: 'badge-red', color: G.red },
  insufficient_data: { texto: 'Sin datos', badge: 'badge-gray', color: G.muted },
};

// Veredicto del backend → español. Se mapean las cadenas exactas que devuelve
// gateway-health.js; cualquier otra se muestra tal cual en vez de tragársela.
const VEREDICTOS = {
  'Can increase': { texto: 'Puede subir', color: G.green },
  'Slow down': { texto: 'Frena', color: G.red },
  'Not enough data': { texto: 'Sin datos suficientes', color: G.muted },
};

const SEVERIDADES = {
  critical: { texto: 'Crítico', badge: 'badge-red', color: G.red },
  warning: { texto: 'Aviso', badge: 'badge-orange', color: G.orange },
  info: { texto: 'Info', badge: 'badge-blue', color: G.blue },
};
const PESO_SEVERIDAD = { critical: 0, warning: 1, info: 2 };

// Recomendaciones en español por código de alerta. Si aparece un código nuevo
// se usa el `whatToDo` que mande el backend.
const QUE_HACER = {
  chargeback_rate_high: 'Pausa el volumen nuevo en esta cuenta, revisa los pedidos disputados buscando un producto o una fuente de tráfico común, y contesta las disputas abiertas antes de que el ratio se acumule.',
  decline_rate_high: 'Mira si hay reintentos repetidos sobre la misma tarjeta y si hay un BIN o un país que rechaza de forma sistemática; manda ese tráfico a otra cuenta en vez de reintentar en esta.',
  volume_spike: 'Reparte lo que queda del día entre tus otras cuentas. Un salto brusco de volumen diario es uno de los disparadores más fuertes de revisión manual.',
  daily_limit_exceeded: 'Deja de mandar cobros nuevos a esta cuenta hoy y rota a la siguiente.',
  young_account: 'Sube el volumen diario poco a poco. Los adquirentes penalizan mucho desviarse pronto del perfil de procesamiento declarado.',
  metrics_hidden: 'Pide que se reactive la visibilidad de pagos rechazados para este tenant si necesitas aquí la tasa de aprobación.',
  insufficient_data: 'Amplía el rango de fechas, o trata esta tarjeta como provisional hasta que la cuenta tenga más historial.',
};

const AVISO_MUESTRA = 'Muestra pequeña: la tasa está calculada sobre pocos casos, tómala como orientación y no como una medición fiable.';

// ── Helpers de formato ──────────────────────────────────────────────────────

// "2026-09-18" → "18/09/2026". No se usa fmtDay() del theme con estas claves:
// ya son días naturales calculados por el backend en la zona del tenant, y
// pasarlas por new Date() las interpreta como UTC y pueden retroceder un día.
function fmtClaveDia(k) {
  const s = String(k || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '--';
  const [a, m, d] = s.split('-');
  return `${d}/${m}/${a}`;
}

function esNum(n) {
  return n !== null && n !== undefined && typeof n === 'number' && isFinite(n);
}

function pct(n, dec = 2) {
  return esNum(n) ? `${n.toFixed(dec)}%` : '--';
}

function num(n) {
  return esNum(n) ? n.toLocaleString('es-ES') : '--';
}

// Verde holgado, ámbar apretado, rojo pasado.
function colorConsumo(p) {
  if (!esNum(p)) return G.muted;
  if (p >= 100) return G.red;
  if (p >= 70) return G.orange;
  return G.green;
}

// Mismos cortes que usa el backend para decidir el estado (>=8 crítica,
// >=5 vigilar), para que el color del medidor no contradiga al badge.
function colorExposicion(i) {
  if (!esNum(i)) return G.muted;
  if (i >= 8) return G.red;
  if (i >= 5) return G.orange;
  return G.green;
}

function etiquetaAntiguedad(g) {
  const psp = String(g.psp || g.provider || 'PASARELA').toUpperCase();
  return esNum(g.ageDays) ? `${psp} · día ${g.ageDays}` : `${psp} · antigüedad desconocida`;
}

function estadoDe(s) { return ESTADOS[s] || ESTADOS.insufficient_data; }

function veredictoDe(g) {
  return VEREDICTOS[g.verdict] || { texto: g.verdict || 'Sin veredicto', color: G.muted };
}

/**
 * Texto en español de una alerta, reconstruido desde los campos estructurados.
 * `win` = bloque window de la respuesta, `mins` = bloque minimums.
 */
function textoAlerta(a, g, win, mins) {
  const c = g.counts || {};
  const v = g.volume || {};
  const dias = esNum(win.days) ? win.days : null;
  const enVentana = dias ? ` en los últimos ${dias} días` : ' en la ventana seleccionada';
  const m = a.measured;
  const t = a.threshold;

  switch (a.code) {
    case 'chargeback_rate_high':
      return `El ${pct(m)} de las ventas acaba en chargeback, el umbral está en ${esNum(t) ? t.toFixed(1) : '--'} — ${num(c.chargebacks)} de ${num(c.approved)} cobros${enVentana}.`;
    case 'decline_rate_high':
      return `De cada 100 intentos de cobro se rechazan ${esNum(m) ? Math.round(m) : '--'}, el umbral está en ${esNum(t) ? t.toFixed(1) : '--'} — ${num(c.failed)} rechazos de ${num(c.attempts)} intentos${enVentana}.`;
    case 'volume_spike':
      // Se compara contra el volumen HASTA ESTA HORA, que es el dato con el que
      // el backend calcula realmente el pico (todayEURToHour vs baseline).
      return `El volumen de hoy va un ${pct(m)} por encima de lo normal a esta hora, el umbral está en ${esNum(t) ? t.toFixed(1) : '--'} — ${fmt(v.todayEURToHour)} hasta ahora frente a ${fmt(v.baselineEURToHour)} habituales a esta hora.`;
    case 'daily_limit_exceeded':
      return `El volumen facturado hoy va por el ${pct(m)} de la capacidad diaria recomendada (${fmt(g.recommendedDailyLimitEUR)}).`;
    case 'young_account':
      return `La cuenta tiene ${num(m)} días, por debajo del umbral de rodaje de ${num(t)} días.`;
    case 'metrics_hidden':
      return 'Las tasas de aprobación y rechazo no se muestran en esta cuenta porque los pagos rechazados están ocultos en este panel.';
    case 'insufficient_data':
      return `No hay suficientes cobros resueltos en la ventana para calcular tasas fiables: ${num(c.attempts)} intentos y ${num(c.approved)} aprobados, hacen falta ${num(mins.attemptsForRates)} intentos y ${num(mins.approvedForChargebackRate)} cobros aprobados.`;
    default:
      return a.message || 'Alerta sin descripción.';
  }
}

// ── Piezas de UI ────────────────────────────────────────────────────────────

/** Medidor segmentado 1-10 del índice de exposición. */
function MedidorExposicion({ indice, provisional }) {
  const color = colorExposicion(indice);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 26, fontWeight: 600, color, lineHeight: 1 }}>
          {esNum(indice) ? indice : '--'}
        </span>
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: G.muted }}>/ 10</span>
        <span style={{ fontSize: 10, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'DM Mono, monospace' }}>
          exposición
        </span>
        {provisional && (
          <span className="tag" title="Faltan datos para alguna de las tasas que alimentan el índice.">provisional</span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 3, marginTop: 8 }}>
        {Array.from({ length: 10 }, (_, i) => {
          const lleno = esNum(indice) && i < indice;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: 7,
                borderRadius: 2,
                background: lleno ? color : G.border,
                opacity: lleno ? 0.5 + (i * 0.05) : 1,
              }}
            />
          );
        })}
      </div>

      <div style={{ fontSize: 10, color: G.muted, marginTop: 7, lineHeight: 1.5 }}>
        Es un índice interno de exposición operativa, no una probabilidad de suspensión.
      </div>
    </div>
  );
}

/** Métrica con estado explícito: valor, contexto de muestra y atenuación. */
function Metrica({ etiqueta, valor, sub, color, atenuada, motivo }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'DM Mono, monospace', marginBottom: 5 }}>
        {etiqueta}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span
          style={{
            fontFamily: 'DM Mono, monospace',
            fontSize: 17,
            fontWeight: 600,
            color: atenuada ? G.muted : (color || G.white),
            opacity: atenuada ? 0.75 : 1,
          }}
        >
          {valor}
        </span>
        {atenuada && (
          <span style={{ color: G.orange, fontSize: 11, cursor: 'help' }} title={motivo || AVISO_MUESTRA}>⚠</span>
        )}
      </div>
      {sub && (
        <div style={{ fontSize: 10, color: G.muted, marginTop: 4, fontFamily: 'DM Mono, monospace' }}>{sub}</div>
      )}
    </div>
  );
}

/** Alerta con su "Qué hacer" desplegable. */
function Alerta({ alerta, texto, recomendacion }) {
  const [abierta, setAbierta] = useState(false);
  const sev = SEVERIDADES[alerta.severity] || SEVERIDADES.info;

  return (
    <div style={{ border: `1px solid ${G.border}`, borderLeft: `2px solid ${sev.color}`, borderRadius: 8, padding: '10px 12px', background: G.black }}>
      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
        <span className={`badge ${sev.badge}`} style={{ flexShrink: 0 }}>{sev.texto}</span>
        <div style={{ fontSize: 12, color: G.white, lineHeight: 1.5, flex: 1, minWidth: 0 }}>{texto}</div>
      </div>
      <button
        className="btn btn-ghost btn-sm"
        style={{ marginTop: 8, fontSize: 10, padding: '4px 10px' }}
        onClick={() => setAbierta(a => !a)}
      >
        {abierta ? '▾ Qué hacer' : '▸ Qué hacer'}
      </button>
      {abierta && (
        <div style={{ marginTop: 8, fontSize: 12, color: G.dim, lineHeight: 1.6, borderTop: `1px solid ${G.border}`, paddingTop: 8 }}>
          {recomendacion}
        </div>
      )}
    </div>
  );
}

/** Fila etiqueta/valor del bloque de detalle. */
function FilaDetalle({ etiqueta, valor, titulo }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0' }} title={titulo}>
      <span style={{ fontSize: 11, color: G.muted }}>{etiqueta}</span>
      <span style={{ fontSize: 11, color: G.dim, fontFamily: 'DM Mono, monospace', textAlign: 'right' }}>{valor}</span>
    </div>
  );
}

/** Tarjeta de una pasarela. */
function TarjetaPasarela({ g, win, mins }) {
  const [detalle, setDetalle] = useState(false);

  const est = estadoDe(g.status);
  const ver = veredictoDe(g);
  const c = g.counts || {};
  const v = g.volume || {};
  const f = g.recommendedDailyLimitFactors || {};
  const br = g.exposureBreakdown || {};
  const tope = g.operatorDailyLimit || {};

  const dias = esNum(win.days) ? `${win.days}d` : 'ventana';

  // Tasas: el backend sólo las calcula por encima de su mínimo, pero una tasa
  // justo por encima del mínimo sigue siendo débil. Se atenúa por debajo del
  // triple del mínimo que el propio backend declara en `minimums`.
  const minIntentos = esNum(mins.attemptsForRates) ? mins.attemptsForRates : 20;
  const minAprobados = esNum(mins.approvedForChargebackRate) ? mins.approvedForChargebackRate : 50;
  const muestraDebilTasas = g.ratesState === 'ok' && esNum(c.attempts) && c.attempts < minIntentos * 3;
  const muestraDebilCb = g.chargebackRateState === 'ok' && esNum(c.approved) && c.approved < minAprobados * 3;

  let valorAprob = pct(g.approvalRatePct);
  let subAprob = `${num(c.attempts)} intentos / ${dias}`;
  if (g.ratesState === 'insufficient_data') {
    valorAprob = '--';
    subAprob = `sin datos suficientes · ${num(c.attempts)} de ${num(minIntentos)} intentos`;
  } else if (g.ratesState === 'hidden_for_tenant') {
    valorAprob = '--';
    subAprob = 'oculta en este panel';
  }

  let valorCb = pct(g.chargebackRatePct);
  let subCb = `${num(c.chargebacks)} de ${num(c.approved)} cobros / ${dias}`;
  if (g.chargebackRateState !== 'ok') {
    valorCb = '--';
    subCb = `sin datos suficientes · ${num(c.approved)} de ${num(minAprobados)} cobros`;
  }

  const consumo = g.percentUsed;
  const colorC = colorConsumo(consumo);
  const anchoBarra = esNum(consumo) ? Math.max(0, Math.min(100, consumo)) : 0;

  const alertas = [...(g.alerts || [])].sort(
    (a, b) => (PESO_SEVERIDAD[a.severity] ?? 3) - (PESO_SEVERIDAD[b.severity] ?? 3)
  );

  return (
    <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="card-head" style={{ gap: 10, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div className="card-title" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {g.name || g.gatewayId || 'Pasarela sin nombre'}
          </div>
          <div style={{ fontSize: 10, color: G.muted, fontFamily: 'DM Mono, monospace', marginTop: 4, letterSpacing: '0.06em' }}>
            {etiquetaAntiguedad(g)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {!g.active && <span className="tag" title="Pasarela desactivada">inactiva</span>}
          <span className={`badge ${est.badge}`}>{est.texto}</span>
        </div>
      </div>

      <div className="card-body" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
        {/* Exposición + veredicto */}
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <MedidorExposicion indice={g.exposureIndex} provisional={!!g.insufficientData} />
          </div>
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: 9, color: G.muted, textTransform: 'uppercase', letterSpacing: '0.14em', fontFamily: 'DM Mono, monospace', marginBottom: 5 }}>
              Veredicto
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: ver.color, whiteSpace: 'nowrap' }}>{ver.texto}</div>
          </div>
        </div>

        {/* Hoy / Límite / Restante */}
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 9 }}>
            <Metrica
              etiqueta="Hoy"
              valor={fmt(v.todayEUR)}
              sub={`${num(v.todayCount)} cobros`}
              color={G.white}
            />
            <Metrica
              etiqueta="Capacidad"
              valor={g.recommendedDailyLimitBasis === 'cold-start' ? '--' : fmt(g.recommendedDailyLimitEUR)}
              sub={g.recommendedDailyLimitBasis === 'cold-start' ? 'sin historial suficiente' : `base: volumen observado (${num(g.recommendedDailyLimitFactors?.activeDays)} días)`}
              color={g.recommendedDailyLimitBasis === 'cold-start' ? G.muted : G.goldLight}
              atenuada={g.recommendedDailyLimitBasis === 'cold-start'}
              motivo="La cuenta no tiene suficientes días con cobros para calcular una capacidad diaria real."
            />
            <Metrica
              etiqueta="Restante"
              valor={g.dailyGaugeApplies && g.recommendedDailyLimitBasis !== 'cold-start' && esNum(g.remainingEUR) ? fmt(g.remainingEUR) : '--'}
              sub={!g.dailyGaugeApplies ? 'la ventana no incluye hoy' : g.recommendedDailyLimitBasis === 'cold-start' ? 'no hay capacidad calculada' : (esNum(consumo) ? `${consumo.toFixed(0)}% consumido` : 'sin porcentaje')}
              color={g.recommendedDailyLimitBasis === 'cold-start' ? G.muted : colorC}
            />
          </div>

          <div className="progress" style={{ height: 6 }}>
            <div
              className="progress-fill"
              style={{ width: g.recommendedDailyLimitBasis === 'cold-start' ? 0 : `${anchoBarra}%`, background: colorC, opacity: g.dailyGaugeApplies ? 1 : 0.3 }}
            />
          </div>
          <div style={{ fontSize: 10, color: G.muted, marginTop: 6, lineHeight: 1.5 }}>
            {g.recommendedDailyLimitBasis === 'cold-start'
              ? 'Sin historial suficiente para calcular capacidad. Necesita al menos 5 días con cobros.'
              : g.dailyGaugeApplies
              ? 'Capacidad diaria orientativa, derivada del historial de esta misma cuenta. No se aplica en ningún sitio.'
              : 'La ventana seleccionada no incluye hoy, así que el consumo del día no se calcula.'}
          </div>
        </div>

        {/* Tasas */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, borderTop: `1px solid ${G.border}`, paddingTop: 14 }}>
          <Metrica
            etiqueta="Aprobación"
            valor={valorAprob}
            sub={subAprob}
            color={esNum(g.approvalRatePct) ? (g.approvalRatePct >= 85 ? G.green : g.approvalRatePct >= 70 ? G.orange : G.red) : G.muted}
            atenuada={muestraDebilTasas}
            motivo={`Muestra pequeña, poco fiable: ${num(c.attempts)} intentos en la ventana.`}
          />
          <Metrica
            etiqueta="Chargebacks"
            valor={valorCb}
            sub={subCb}
            color={esNum(g.chargebackRatePct) ? (g.chargebackRatePct >= 1 ? G.red : g.chargebackRatePct >= 0.65 ? G.orange : G.green) : G.muted}
            atenuada={muestraDebilCb}
            motivo={`Muestra pequeña, poco fiable: ${num(c.approved)} cobros aprobados en la ventana.`}
          />
        </div>

        {/* Alertas */}
        {alertas.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: `1px solid ${G.border}`, paddingTop: 14 }}>
            {alertas.map((a, i) => (
              <Alerta
                key={`${a.code || 'alerta'}-${i}`}
                alerta={a}
                texto={textoAlerta(a, g, win, mins)}
                recomendacion={QUE_HACER[a.code] || a.whatToDo || 'Sin recomendación asociada.'}
              />
            ))}
          </div>
        )}

        {/* Detalle */}
        <div style={{ marginTop: 'auto', borderTop: `1px solid ${G.border}`, paddingTop: 12 }}>
          <button className="btn btn-ghost btn-sm" style={{ fontSize: 10 }} onClick={() => setDetalle(d => !d)}>
            {detalle ? '▾ Menos detalle' : '▸ Cómo sale este número'}
          </button>
          {detalle && (
            <div style={{ marginTop: 10 }}>
              <FilaDetalle etiqueta="Mediana diaria (días con cobros)" valor={esNum(f.stableDailyEUR) ? fmt(f.stableDailyEUR) : '--'} />
              <FilaDetalle etiqueta="Pico diario en la ventana" valor={esNum(f.peakDailyEUR) ? fmt(f.peakDailyEUR) : '--'} />
              <FilaDetalle etiqueta="Días con actividad" valor={num(f.activeDays)} />
              <FilaDetalle etiqueta="Factor de crecimiento aplicado" valor={esNum(f.growth) ? `×${f.growth}` : '--'} titulo="Antigüedad × aprobación × chargebacks" />
              <FilaDetalle
                etiqueta="Puntos de exposición"
                valor={`CB ${num(br.chargeback)} · rechazos ${num(br.decline)} · edad ${num(br.age)} · pico ${num(br.volumeSpike)}`}
                titulo="Desglose del índice 1-10 (base 1 + puntos)"
              />
              <FilaDetalle
                etiqueta="Cobros en la ventana"
                valor={`${num(c.succeeded)} OK · ${num(c.refunded)} devueltos · ${num(c.failed)} rechazados · ${num(c.chargebacks)} CB`}
              />
              <FilaDetalle
                etiqueta="Volumen hasta esta hora"
                valor={`${fmt(v.todayEURToHour)} vs ${esNum(v.baselineEURToHour) ? fmt(v.baselineEURToHour) : '--'} normal`}
                titulo={esNum(v.baselineDays) ? `Media de ${v.baselineDays} mismos días de la semana anteriores` : 'Sin base de comparación suficiente'}
              />
              <FilaDetalle
                etiqueta="Tope propio del operador"
                valor={tope.configured ? `${fmt(tope.amountEUR)}${tope.orders ? ` · ${num(tope.orders)} pedidos` : ''}` : 'sin tope configurado'}
              />
              <FilaDetalle etiqueta="Cuenta creada" valor={g.createdAt ? fmtDay(g.createdAt) : '--'} />
              <FilaDetalle
                etiqueta="Próximo payout"
                valor="--"
                titulo={(g.nextPayout && g.nextPayout.reason) || 'No se guarda localmente.'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sección ─────────────────────────────────────────────────────────────────

export default function GatewayHealth({ showToast }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(false);

  // Filtros aplicados (los que van al servidor) vs. borrador del rango manual,
  // mismo patrón que la sección Clientes.
  const [aplicado, setAplicado] = useState({ preset: '30d', from: '', to: '', activeOnly: false });
  const [rango, setRango] = useState({ from: '', to: '' });
  const [modoRango, setModoRango] = useState(false);

  const [orden, setOrden] = useState('riesgo');
  const [recarga, setRecarga] = useState(0);

  // showToast se guarda en una ref: si el padre lo recrea en cada render, una
  // dependencia directa en el efecto de carga lo relanzaría en bucle.
  const toastRef = useRef(showToast);
  useEffect(() => { toastRef.current = showToast; }, [showToast]);
  const avisar = useCallback((msg, tipo) => {
    const f = toastRef.current;
    if (typeof f === 'function') f(msg, tipo);
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true);
      try {
        const params = { activeOnly: aplicado.activeOnly ? '1' : '0' };
        if (aplicado.from && aplicado.to) {
          params.from = aplicado.from;
          params.to = aplicado.to;
        } else {
          params.window = aplicado.preset;
        }
        const res = await gatewayHealthApi.get(params);
        if (!vivo) return;
        setData(res || null);
        setFallo(false);
      } catch (e) {
        if (!vivo) return;
        setData(null);
        setFallo(true);
        avisar('No se pudo cargar la salud de las pasarelas', 'error');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, [aplicado, recarga, avisar]);

  const win = (data && data.window) || {};
  const mins = (data && data.minimums) || {};

  const pasarelas = useMemo(() => {
    const arr = [...((data && data.gateways) || [])];
    if (orden === 'nombre') {
      arr.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'es'));
    } else if (orden === 'volumen') {
      arr.sort((a, b) => ((b.volume && b.volume.todayEUR) || 0) - ((a.volume && a.volume.todayEUR) || 0));
    } else {
      // Riesgo: más crítica primero, y a igualdad, la que más ha facturado hoy.
      arr.sort((a, b) =>
        ((b.exposureIndex || 0) - (a.exposureIndex || 0)) ||
        (((b.volume && b.volume.todayEUR) || 0) - ((a.volume && a.volume.todayEUR) || 0))
      );
    }
    return arr;
  }, [data, orden]);

  const resumen = useMemo(() => {
    const gs = (data && data.gateways) || [];
    const conMedidor = gs.filter(g => g.dailyGaugeApplies && esNum(g.remainingEUR));
    return {
      total: gs.length,
      criticas: gs.filter(g => g.status === 'critical').length,
      aviso: gs.filter(g => g.status === 'warning').length,
      sinDatos: gs.filter(g => g.status === 'insufficient_data').length,
      hoy: gs.reduce((a, g) => a + ((g.volume && g.volume.todayEUR) || 0), 0),
      restante: conMedidor.reduce((a, g) => a + (g.remainingEUR || 0), 0),
      conMedidor: conMedidor.length,
      alertas: gs.reduce((a, g) => a + ((g.alerts || []).filter(x => x.severity === 'critical').length), 0),
    };
  }, [data]);

  const elegirPreset = (id) => {
    setModoRango(false);
    setAplicado(a => ({ ...a, preset: id, from: '', to: '' }));
  };

  const aplicarRango = () => {
    if (!rango.from || !rango.to) {
      avisar('Elige fecha de inicio y de fin', 'error');
      return;
    }
    setAplicado(a => ({ ...a, from: rango.from, to: rango.to }));
  };

  const cambiarActivas = (solo) => setAplicado(a => ({ ...a, activeOnly: solo }));

  const hayDatos = pasarelas.length > 0;

  return (
    <div>
      {/* Descargo general, discreto pero antes que cualquier número. */}
      <div style={{ fontSize: 11, color: G.muted, lineHeight: 1.6, marginBottom: 18, maxWidth: 860 }}>
        La capacidad diaria recomendada es <strong style={{ color: G.dim, fontWeight: 600 }}>orientativa</strong>: sale del historial
        de cada cuenta y no la aplica nadie. El índice de exposición 1-10 es un índice interno de exposición operativa, no una
        probabilidad de suspensión. Las tasas calculadas sobre pocos intentos aparecen atenuadas: no son comparables con las de una
        cuenta con cientos de cobros.
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label">Pasarelas</div>
          <div className="kpi-value">{num(resumen.total)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {aplicado.activeOnly ? 'sólo activas' : 'todas las cuentas'}
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">En riesgo</div>
          <div className="kpi-value" style={{ WebkitTextFillColor: resumen.criticas ? G.red : undefined }}>
            {num(resumen.criticas)}
          </div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {num(resumen.aviso)} a vigilar · {num(resumen.sinDatos)} sin datos
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Facturado hoy</div>
          <div className="kpi-value">{fmt(resumen.hoy)}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>suma de todas las cuentas</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Capacidad restante</div>
          <div className="kpi-value">{resumen.conMedidor ? fmt(resumen.restante) : '--'}</div>
          <div className="kpi-delta" style={{ color: G.muted }}>
            {resumen.conMedidor ? `orientativa · ${num(resumen.conMedidor)} cuentas` : 'la ventana no incluye hoy'}
          </div>
        </div>
      </div>

      {/* Controles */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-head">
          <div className="card-title">Salud de pasarelas</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="pill-group">
              {ORDENES.map(o => (
                <button
                  key={o.id}
                  className={`pill ${orden === o.id ? 'active' : ''}`}
                  onClick={() => setOrden(o.id)}
                  title={o.id === 'riesgo' ? 'Primero la más crítica' : undefined}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setRecarga(n => n + 1)}>↻ Refrescar</button>
          </div>
        </div>

        <div className="card-body">
          <div className="filters" style={{ marginBottom: 0 }}>
            <div className="pill-group">
              {PRESETS.map(p => (
                <button
                  key={p.id}
                  className={`pill ${(!modoRango && !aplicado.from && aplicado.preset === p.id) ? 'active' : ''}`}
                  onClick={() => elegirPreset(p.id)}
                >
                  {p.label}
                </button>
              ))}
              <button
                className={`pill ${(modoRango || aplicado.from) ? 'active' : ''}`}
                onClick={() => setModoRango(true)}
              >
                Rango
              </button>
            </div>

            {(modoRango || aplicado.from) && (
              <>
                <input
                  type="date"
                  className="form-input"
                  style={{ width: 'auto', padding: '8px 12px', fontSize: 12 }}
                  value={rango.from}
                  max={win.today || undefined}
                  onChange={e => setRango(r => ({ ...r, from: e.target.value }))}
                />
                <span style={{ color: G.muted, fontSize: 12 }}>→</span>
                <input
                  type="date"
                  className="form-input"
                  style={{ width: 'auto', padding: '8px 12px', fontSize: 12 }}
                  value={rango.to}
                  max={win.today || undefined}
                  onChange={e => setRango(r => ({ ...r, to: e.target.value }))}
                />
                <button className="btn btn-gold btn-sm" onClick={aplicarRango}>Aplicar</button>
              </>
            )}

            <div className="pill-group">
              <button className={`pill ${!aplicado.activeOnly ? 'active' : ''}`} onClick={() => cambiarActivas(false)}>Todas</button>
              <button className={`pill ${aplicado.activeOnly ? 'active' : ''}`} onClick={() => cambiarActivas(true)}>Sólo activas</button>
            </div>

            <div style={{ fontSize: 11, color: G.muted, fontFamily: 'DM Mono, monospace', marginLeft: 'auto' }}>
              {win.from
                ? `${fmtClaveDia(win.from)} – ${fmtClaveDia(win.to)} · ${num(win.days)} días · ${win.timezone || '--'}`
                : 'sin ventana'}
            </div>
          </div>

          {data && data.declineMetricsHidden && (
            <div style={{ marginTop: 14, fontSize: 11, color: G.orange, background: G.orangeDim, border: `1px solid ${G.orange}25`, borderRadius: 8, padding: '9px 12px', lineHeight: 1.5 }}>
              Los pagos rechazados están ocultos en este panel, así que las tasas de aprobación y rechazo no se calculan para ninguna
              cuenta. Se muestran como "--" en vez de un 100% falso.
            </div>
          )}
        </div>
      </div>

      {/* Tarjetas */}
      {cargando && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 44, color: G.muted, fontSize: 13 }}>
            Cargando salud de pasarelas…
          </div>
        </div>
      )}

      {!cargando && fallo && (
        <div className="card">
          <div className="card-body empty" style={{ textAlign: 'center', padding: 44 }}>
            <div className="empty-icon">⚡</div>
            <div style={{ fontSize: 14, color: G.dim, marginBottom: 6 }}>No se pudieron cargar los datos</div>
            <div style={{ fontSize: 12, color: G.muted, marginBottom: 16 }}>
              El panel no ha podido hablar con el servidor. No se muestra nada antes que mostrar números viejos o inventados.
            </div>
            <button className="btn btn-gold btn-sm" onClick={() => setRecarga(n => n + 1)}>Reintentar</button>
          </div>
        </div>
      )}

      {!cargando && !fallo && !hayDatos && (
        <div className="card">
          <div className="card-body empty" style={{ textAlign: 'center', padding: 44 }}>
            <div className="empty-icon">🛡</div>
            <div style={{ fontSize: 14, color: G.dim, marginBottom: 6 }}>No hay pasarelas que analizar</div>
            <div style={{ fontSize: 12, color: G.muted }}>
              {aplicado.activeOnly
                ? 'No tienes ninguna pasarela activa. Prueba con "Todas" para ver también las desactivadas.'
                : 'En cuanto conectes una pasarela y empiece a cobrar, aquí verás su antigüedad, sus tasas y cuánto volumen admite.'}
            </div>
          </div>
        </div>
      )}

      {!cargando && !fallo && hayDatos && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 18, alignItems: 'start' }}>
          {pasarelas.map(g => (
            <TarjetaPasarela key={g.gatewayId || g.name} g={g} win={win} mins={mins} />
          ))}
        </div>
      )}
    </div>
  );
}
